// src/App.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from 'contexts/AuthContext';
import { AppProvider }           from 'contexts/AppContext';
import Layout                    from 'shared/components/layout/Layout';
import { isAdminRole, ADMIN_ROLES } from 'shared/config/roles';

// ── Auth ───────────────────────────────────────────────────────────────────────
import Login from 'domains/auth/pages/Login';
import Setup from 'domains/auth/pages/Setup';

// ── Onboarding (público) ───────────────────────────────────────────────────────
import ClinicSignupPage          from 'domains/onboarding/pages/ClinicSignupPage';
import ProfessionalSignupPage    from 'domains/onboarding/pages/ProfessionalSignupPage';
import PlanSelectionPage         from 'domains/onboarding/pages/PlanSelectionPage';
import PaymentPlaceholderPage    from 'domains/onboarding/pages/PaymentPlaceholderPage';

// ── Catálogo (cliente) ─────────────────────────────────────────────────────────
import CatalogPage       from 'domains/catalog/pages/client/CatalogPage';
import ClinicProfilePage from 'domains/catalog/pages/client/ClinicProfilePage';

// ── Perfil do profissional ─────────────────────────────────────────────────────
import ProfessionalProfilePage from 'domains/dashboard/pages/professional/ProfilePage';

// ── Chat ───────────────────────────────────────────────────────────────────────
import ChatPage from 'domains/chat/pages/ChatPage';

// ── Dashboard ──────────────────────────────────────────────────────────────────
import AdminDashboard        from 'domains/dashboard/pages/admin/DashboardPage';
import ProfessionalDashboard from 'domains/dashboard/pages/professional/DashboardPage';

// ── Appointments ───────────────────────────────────────────────────────────────
import AdminAppointments from 'domains/appointments/pages/admin/AppointmentsPage';
import ClientHome        from 'domains/appointments/pages/client/HomePage';
import ClientAgenda      from 'domains/appointments/pages/client/AgendaPage';
import NewAppointment    from 'domains/appointments/components/NewAppointmentForm';

// ── Procedures ─────────────────────────────────────────────────────────────────
import AdminProcedures        from 'domains/procedures/pages/admin/ProceduresPage';
import ProfessionalProcedures from 'domains/procedures/pages/professional/ProceduresPage';

// ── Scheduling ─────────────────────────────────────────────────────────────────
import AdminScheduleWindows from 'domains/scheduling/pages/admin/ScheduleWindowsPage';
import ProfessionalSchedule from 'domains/scheduling/pages/professional/SchedulePage';

// ── Users ──────────────────────────────────────────────────────────────────────
import AdminClients       from 'domains/users/pages/admin/ClientsPage';
import AdminProfessionals from 'domains/users/pages/admin/ProfessionalsPage';
import AdminUsers         from 'domains/users/pages/admin/UsersPage';

// ── Cashflow & Settings ────────────────────────────────────────────────────────
import AdminCashflow from 'domains/cashflow/pages/admin/CashflowPage';
import AdminSettings from 'domains/settings/pages/admin/SettingsPage';

// ── Dev / Seed ─────────────────────────────────────────────────────────────────
import SeedPage from 'domains/dev/SeedPage';

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                  minHeight:'100vh', background:'#f8f7f5' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:44, height:44, border:'4px solid #e5e2da',
                      borderTopColor:'#e94560', borderRadius:'50%', margin:'0 auto 16px',
                      animation:'spin 0.8s linear infinite' }} />
        <div style={{ fontFamily:"'DM Sans',sans-serif", color:'#a8a49d', fontSize:14 }}>
          Carregando...
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

// ─── Guard de rota privada ─────────────────────────────────────────────────────
function PrivateRoute({ children, roles }) {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading)          return <Spinner />;
  if (!currentUser)     return <Navigate to="/login" replace />;
  if (!userProfile)     return <Spinner />;
  if (!userProfile.role) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(userProfile.role)) return <Navigate to="/" replace />;
  return children;
}

// ─── Redirecionamento por perfil ───────────────────────────────────────────────
function RoleRedirect() {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!currentUser) return <Navigate to="/login" replace />;
  // Espera o perfil carregar — SEM isso, admin/profissional caía em /client/home
  // durante a janela em que userProfile ainda é null.
  if (!userProfile) return <Spinner />;
  if (isAdminRole(userProfile.role))       return <Navigate to="/admin/dashboard" replace />;
  if (userProfile.role === 'professional') return <Navigate to="/pro/dashboard"   replace />;
  if (userProfile.role === 'client')       return <Navigate to="/client/home"     replace />;
  // Role desconhecido/ausente: volta ao login em vez de assumir cliente.
  return <Navigate to="/login" replace />;
}

// ─── Rotas ────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <Routes>
            {/* Públicas */}
            <Route path="/login"              element={<Login />} />
            <Route path="/setup"              element={<Setup />} />
            <Route path="/signup/clinic"        element={<ClinicSignupPage />} />
            <Route path="/signup/professional" element={<ProfessionalSignupPage />} />
            <Route path="/signup/plan"         element={<PlanSelectionPage />} />
            <Route path="/signup/payment"      element={<PaymentPlaceholderPage />} />

            <Route path="/" element={<PrivateRoute><RoleRedirect /></PrivateRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<PrivateRoute roles={ADMIN_ROLES}><Layout role="admin" /></PrivateRoute>}>
              <Route path="dashboard"        element={<AdminDashboard />} />
              <Route path="appointments"     element={<AdminAppointments />} />
              <Route path="new-appointment"  element={<NewAppointment />} />
              <Route path="cashflow"         element={<AdminCashflow />} />
              <Route path="procedures"       element={<AdminProcedures />} />
              <Route path="professionals"    element={<AdminProfessionals />} />
              <Route path="clients"          element={<AdminClients />} />
              <Route path="users"            element={<AdminUsers />} />
              <Route path="schedule-windows" element={<AdminScheduleWindows />} />
              <Route path="settings"         element={<AdminSettings />} />
              <Route path="seed"             element={<SeedPage />} />
            </Route>

            {/* Profissional */}
            <Route path="/pro" element={<PrivateRoute roles={['professional', ...ADMIN_ROLES]}><Layout role="professional" /></PrivateRoute>}>
              <Route path="dashboard"       element={<ProfessionalDashboard />} />
              <Route path="schedule"        element={<ProfessionalSchedule />} />
              <Route path="new-appointment" element={<NewAppointment />} />
              <Route path="procedures"      element={<ProfessionalProcedures />} />
              <Route path="profile"         element={<ProfessionalProfilePage />} />
              <Route path="chat/:appointmentId" element={<ChatPage />} />
            </Route>

            {/* Cliente */}
            <Route path="/client" element={<PrivateRoute roles={['client', ...ADMIN_ROLES]}><Layout role="client" /></PrivateRoute>}>
              <Route path="home"               element={<ClientHome />} />
              <Route path="agenda"             element={<ClientAgenda />} />
              <Route path="new-appointment"    element={<NewAppointment />} />
              <Route path="catalog"            element={<CatalogPage />} />
              <Route path="catalog/:category"  element={<CatalogPage />} />
              <Route path="clinic/:id"         element={<ClinicProfilePage />} />
              <Route path="chat/:appointmentId" element={<ChatPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}
